import {
  getGridStringOperators,
  GridColDef,
  GridFilterInputValue,
  GridFilterOperator,
} from "@mui/x-data-grid-pro"
import { StudentAcademicStatusDTO } from "../../studentAcademicStatus.dto"
import { Box, Chip, Link, Tooltip, Typography } from "@mui/material"
import { cohortColor } from "../display.utils"
import { dateStrComparator } from "./column.sort.comparators"
import { ColumnKeys } from "./column.keys"
import { DomainChip } from "@utils/domainChip"
import { domainToDisplayName } from "@utils/domainUtils"
import { ProgramDomain } from "@masterschool/course-builder-api"
import { LanguageChip } from "../../../../programs/program/components/languageChip"
import { formatProgramStartDate } from "../../programLabel"

// One glance answers "who is this enrolled with": Program · Cohort · Class in
// a single default cell. School / Cohort / Class remain in the chooser as
// separate sortable columns; this composite replaces them as the default.
// The header names the three facts instead of the jargon word "Enrollment" —
// even Mayer couldn't say what an "Enrollment" was from the label alone. It
// stays singular because the table is one row per student: the cell holds the
// dominant (latest-cohort) registration and the rest fold into the "N more"
// chip.
export const ENROLLMENT_COLUMN: GridColDef<StudentAcademicStatusDTO> = {
  field: ColumnKeys.ENROLLMENT,
  headerName: "Program · Cohort · Class",
  flex: 1.6,
  // Width note: the DEFAULT column set must fit a 1680-wide window (1392px
  // grid viewport) with no horizontal scrollbar — a permanently-overflowing
  // grid pans sideways under the pinned columns on any diagonal trackpad
  // scroll, which reads as constant layout shift. Every default width below
  // and in the sibling column files is budgeted against that 1392 (the
  // flex columns absorb the slack the 130px single-Campus Actions column
  // leaves — the LMS view-as was removed, students have no LMS access).
  minWidth: 270,
  valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
    const registration = studentRow.registrationDetails
    if (!registration) return null
    return [
      domainToDisplayName(registration.domain),
      formatProgramStartDate(registration.cohort),
      registration.className,
    ]
      .filter(Boolean)
      .join(" · ")
  },
  renderCell: (params) => {
    const registration = params.row.registrationDetails
    if (!registration) return null
    const prefix = [
      domainToDisplayName(registration.domain),
      formatProgramStartDate(registration.cohort),
    ]
      .filter(Boolean)
      .join(" · ")
    // Prefer the full-identity fold; fall back to bare class names for older
    // campus responses that predate additionalRegistrations.
    const additionalRegistrations = params.row.additionalRegistrations ?? []
    const additionalClassNames = params.row.additionalClassNames ?? []
    const foldedCount =
      additionalRegistrations.length || additionalClassNames.length
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          height: "100%",
          minWidth: 0,
        }}
      >
        <Tooltip title={params.value ?? ""}>
          <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
            {prefix ? `${prefix} · ` : ""}
            <Link
              href={`/program/${registration.programId}/${registration.classId}`}
              target="_blank"
              onClick={(event) => event.stopPropagation()}
            >
              {registration.className}
            </Link>
          </Typography>
        </Tooltip>
        {foldedCount > 0 && (
          // Folded registrations named as real programs (Program · Cohort ·
          // Class), so the count never reads as bare classes. The caption says
          // "programs" — a multi-program student is not a list of class names.
          <Tooltip
            title={
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {/* body3, not caption: the theme's caption is uppercase by
                    design and this is sentence-case copy. */}
                <Typography variant="body3_sb" color="inherit">
                  {foldedCount} more{" "}
                  {foldedCount === 1 ? "program" : "programs"}
                </Typography>
                {additionalRegistrations.length > 0
                  ? additionalRegistrations.map((other) => (
                      <Typography
                        key={other.classId}
                        variant="body3"
                        color="inherit"
                      >
                        {[
                          domainToDisplayName(other.domain),
                          formatProgramStartDate(other.cohort),
                          other.className,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    ))
                  : additionalClassNames.map((className) => (
                      <Typography
                        key={className}
                        variant="body3"
                        color="inherit"
                      >
                        {className}
                      </Typography>
                    ))}
              </Box>
            }
          >
            <Chip size="small" label={`${foldedCount} more`} />
          </Tooltip>
        )}
      </Box>
    )
  },
}

// "is" on the Class column: an exact class-name match that also honors the
// row's folded additional registrations — the plain string operators only see
// the dominant class, so a student whose OTHER class matches would silently
// drop out. The default string operators stay alongside it, so legacy saved
// views built in the old grid filter panel keep applying unchanged.
const classIsOperator: GridFilterOperator<StudentAcademicStatusDTO> = {
  value: "is",
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.value) return null
    return (value, row) =>
      row.registrationDetails?.className === filterItem.value ||
      (row.additionalClassNames ?? []).includes(filterItem.value)
  },
  InputComponent: GridFilterInputValue,
}

export const REGISTRATION_COLUMNS: GridColDef<StudentAcademicStatusDTO>[] = [
  {
    field: ColumnKeys.PROGRAM,
    headerName: "School",
    width: 150,
    type: "singleSelect",
    valueOptions: schoolOptions,
    valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
      const domain = studentRow.registrationDetails?.domain
      if (!domain) return null
      return domainToDisplayName(domain)
    },
    renderCell: (params) => {
      const classData = params.row.registrationDetails
      if (!classData) return null
      const domain = classData.domain
      return <DomainChip domain={domain} />
    },
  },
  {
    field: ColumnKeys.COHORT,
    headerName: "Cohort",
    width: 150,
    type: "singleSelect",
    valueOptions: cohortOptions(),
    valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
      const cohort = studentRow.registrationDetails?.cohort
      if (!cohort) return null
      return new Date(cohort).toLocaleString("en-US", {
        month: "long",
        year: "2-digit",
      })
    },
    getSortComparator(sortDirection) {
      return (v1, v2) => dateStrComparator(v1, v2, sortDirection)
    },
    renderCell: (params) => {
      const cohort = params.row.registrationDetails?.cohort
      return (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: cohort ? cohortColor(cohort) : undefined,
          }}
        />
      )
    },
  },
  {
    field: ColumnKeys.REGISTERED_CLASS,
    headerName: "Class",
    width: 200,
    filterOperators: [classIsOperator, ...getGridStringOperators()],
    valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
      return studentRow.registrationDetails?.className
    },
    renderCell: (params) => {
      const classData = params.row.registrationDetails
      if (!classData) return null
      const programId = classData.programId
      const classId = classData.classId
      const href = `/program/${programId}/${classId}`
      return (
        <Tooltip title={classData.className}>
          <Link href={href} target="_blank" variant="body1">
            {classData.className}
          </Link>
        </Tooltip>
      )
    },
  },
  {
    field: ColumnKeys.LANGUAGE,
    headerName: "Language",
    width: 160,
    valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
      return studentRow.registrationDetails?.language
    },
    renderCell: (params) => {
      const language = params.row.registrationDetails?.language
      if (!language) return null
      return <LanguageChip language={language} />
    },
  },
]

// "Who starts next" for the Upcoming tab: days until the cohort start date.
// Hidden on the other tabs (their cohorts already started).
export const START_DATE_COLUMN: GridColDef<StudentAcademicStatusDTO> = {
  field: ColumnKeys.START_DATE,
  headerName: "Starts",
  width: 170,
  type: "number",
  valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
    const cohort = studentRow.registrationDetails?.cohort
    if (!cohort) return null
    const start = new Date(cohort)
    if (isNaN(start.getTime())) return null
    const msPerDay = 1000 * 60 * 60 * 24
    const today = new Date()
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    )
    const startMidnight = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    )
    return Math.round(
      (startMidnight.getTime() - todayMidnight.getTime()) / msPerDay,
    )
  },
  renderCell: (params) => {
    const cohort = params.row.registrationDetails?.cohort
    if (!cohort || params.value === null) return null
    const dateText = new Date(cohort).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })
    const days = params.value as number
    const relative =
      days > 1
        ? `in ${days} days`
        : days === 1
        ? "tomorrow"
        : days === 0
        ? "today"
        : "started"
    return `${relative} · ${dateText}`
  },
}

// The Completed tab's exit grouping: graduates are not a generic "ended" row.
// Values come straight from the registration's end_reason; unknown reasons
// display as-is rather than being folded into a bucket.
export const OUTCOME_COLUMN: GridColDef<StudentAcademicStatusDTO> = {
  field: ColumnKeys.OUTCOME,
  headerName: "Outcome",
  width: 140,
  valueGetter: (value, studentRow: StudentAcademicStatusDTO) => {
    const endReason = studentRow.registrationDetails?.endReason
    if (!endReason) return "Ended"
    return endReason.charAt(0).toUpperCase() + endReason.slice(1)
  },
  renderCell: (params) => {
    if (!params.value) return null
    // Status chip semantics: read-only soft fill per outcome family, never
    // outlined — an outlined chip on this page reads as an unselected choice.
    return (
      <Chip
        label={params.value}
        size="small"
        color={params.value === "Graduate" ? "success" : "default"}
        variant={params.value === "Graduate" ? "pastel" : "filled"}
      />
    )
  },
}

export function schoolOptions() {
  return Object.values(ProgramDomain)
    .filter((domain) => !domain.includes("TRAINING"))
    .map((domain) => domainToDisplayName(domain))
}

function cohortOptions() {
  const last14Months = Array.from({ length: 14 }, (_, i) => {
    const date = new Date()
    date.setMonth(date.getMonth() - i)
    return date
  })
  return last14Months.map((date) =>
    date.toLocaleString("en-US", { month: "long", year: "2-digit" }),
  )
}
